import React from "react";
import {
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  buildAudienceNarrative,
  buildAudienceStatusBadges,
  normalizePositiveInteger,
  targetAudienceLabel,
} from "./audienceUtils";

export default function CampaignConfirmation({
  audienceChoice,
  filters,
  audienceSummary,
  selectedIds,
  audiencePlan,
  templateName,
  messagesPerHour,
  cooldownHours,
  sendWindowStart,
  sendWindowEnd,
  targetAudience,
  timezone,
}) {
  const audienceRule = targetAudience || "all";
  const audienceNarrative = buildAudienceNarrative({
    audienceKey: audienceChoice,
    filters,
    summary: audienceSummary,
  });
  const badges = buildAudienceStatusBadges(audienceSummary);
  const preferredLimit = normalizePositiveInteger(audienceSummary?.preferredLimit || filters?.preferred_limit);
  const selectedCount = selectedIds instanceof Set ? selectedIds.size : Array.isArray(selectedIds) ? selectedIds.length : 0;

  return (
    <Stack spacing={2.5}>
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #E5E7EB" }}>
        <Stack spacing={1.25}>
          <Typography variant="subtitle1" fontWeight={900}>
            Confirm the audience
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {audienceNarrative}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`Matched ${badges.matched.toLocaleString()}`} />
            <Chip color="primary" label={`Will send ${badges.sendable.toLocaleString()}`} />
            <Chip color="warning" label={`Will skip ${badges.skipped.toLocaleString()}`} />
            {audiencePlan?.manualSelection ? (
              <Chip color="success" variant="outlined" label={`Picked ${selectedCount.toLocaleString()}`} />
            ) : null}
            {preferredLimit ? (
              <Chip variant="outlined" label={`Preferred limit ${preferredLimit.toLocaleString()}`} />
            ) : null}
            <Chip variant="outlined" label={`Audience: ${targetAudienceLabel(audienceRule)}`} />
          </Stack>
          {audiencePlan?.manualSelection && audienceRule !== "all" ? (
            <Typography variant="body2" color="warning.main">
              Picked contacts outside "{targetAudienceLabel(audienceRule)}" will be skipped when the campaign launches.
            </Typography>
          ) : null}
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #E5E7EB" }}>
        <Stack spacing={1.25}>
          <Typography variant="subtitle1" fontWeight={900}>
            Confirm the message settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Template: <strong>{templateName || "No template selected yet"}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Throughput: <strong>{Number(messagesPerHour || 0).toLocaleString()} per hour</strong> with a <strong>{Number(cooldownHours || 0).toLocaleString()} hour</strong> cooldown.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Send window: <strong>{sendWindowStart || "08:00"} – {sendWindowEnd || "17:00"}</strong> daily. Messages queue up and go out at the hourly rate only inside this window.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Timezone: <strong>{timezone || "Africa/Harare"}</strong>
          </Typography>
          <Paper
            elevation={0}
            sx={{
              mt: 0.5,
              p: 1.5,
              borderRadius: 2.5,
              border: "1px solid rgba(34,197,94,0.18)",
              bgcolor: "rgba(240,253,244,0.9)",
            }}
          >
            <Stack spacing={0.5}>
              <Typography variant="subtitle2" fontWeight={900} sx={{ color: "#166534" }}>
                Reply routing rule
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Replies go to the campaign launcher first, then fallback if that owner is unavailable.
              </Typography>
            </Stack>
          </Paper>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #E5E7EB" }}>
        <Stack spacing={1}>
          <Typography variant="subtitle1" fontWeight={900}>
            Ready to schedule
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The final button below is only for choosing the send time and launching the broadcast.
          </Typography>
        </Stack>
      </Paper>
    </Stack>
  );
}
